/**
 * cloudProjectImportJobs.logic.ts — pure logic for resolving which project
 * import jobs are still "in flight" and which cloud catalog rows they should
 * suppress.
 *
 * Extracted from `cloudProjectsGroup.logic.ts`: everything below is about
 * matching a `ProjectImportJob` against a connected project, a runtime row,
 * or a pre-connect catalog row (by repository label, then by project name as
 * a fallback), plus the TTL that expires a stuck "cloning" job. None of it
 * touches cloud/local key derivation or runtime status, which is what stayed
 * behind — the two clusters shared no state and no helper functions.
 *
 * No React imports — every function is a pure transform.
 */
import type { CmmdForgeRuntimeListSnapshot } from "../../cmmd/cmmdForgeRuntimeListResource";
import type { ProjectImportJob } from "../../projectImportStore";
import type { SidebarProjectSnapshot } from "./SidebarProjectItem";

const STALE_PROJECT_IMPORT_JOB_TTL_MS = 2 * 60 * 60 * 1000;

function normalizeProjectImportMatchValue(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  return trimmed.toLowerCase();
}

function normalizeProjectImportRepositoryValue(value: string | null | undefined): string | null {
  const normalized = normalizeProjectImportMatchValue(value);
  if (!normalized) return null;
  return normalized
    .replace(/^https?:\/\/github\.com\//, "")
    .replace(/^git@github\.com:/, "")
    .replace(/^github\.com\//, "")
    .replace(/\.git$/i, "");
}

function projectImportLabelMatchesRepository(job: ProjectImportJob, value: string | null): boolean {
  const label = normalizeProjectImportRepositoryValue(job.label);
  const repository = normalizeProjectImportRepositoryValue(value);
  if (!label || !repository) return false;
  if (label.includes("/")) return label === repository;
  return repository.split("/").at(-1) === label;
}

function projectImportLabelMatchesProjectName(
  job: ProjectImportJob,
  value: string | null,
): boolean {
  const label = normalizeProjectImportRepositoryValue(job.label);
  const projectName = normalizeProjectImportMatchValue(value);
  return label?.split("/").at(-1) === projectName;
}

function projectImportJobHasQualifiedRepositoryLabel(job: ProjectImportJob): boolean {
  return normalizeProjectImportRepositoryValue(job.label)?.includes("/") ?? false;
}

function projectImportJobMatchesFallbackProjectName(
  job: ProjectImportJob,
  projectName: string | null,
  repositoryValues: readonly (string | null)[],
): boolean {
  const hasRepositoryIdentity = repositoryValues.some(
    (value) => normalizeProjectImportRepositoryValue(value) !== null,
  );
  if (projectImportJobHasQualifiedRepositoryLabel(job) && hasRepositoryIdentity) return false;
  return projectImportLabelMatchesProjectName(job, projectName);
}

function projectImportJobMatchesConnectedProject(
  job: ProjectImportJob,
  project: SidebarProjectSnapshot,
): boolean {
  const repositoryFullName =
    project.repositoryIdentity?.owner && project.repositoryIdentity.name
      ? `${project.repositoryIdentity.owner}/${project.repositoryIdentity.name}`
      : null;
  const canonicalKey = project.repositoryIdentity?.canonicalKey ?? null;
  if (projectImportLabelMatchesRepository(job, repositoryFullName)) return true;
  if (projectImportLabelMatchesRepository(job, canonicalKey)) return true;
  return projectImportJobMatchesFallbackProjectName(job, project.name, [
    repositoryFullName,
    canonicalKey,
  ]);
}

function runtimeRowCanResolveProjectImport(
  row: CmmdForgeRuntimeListSnapshot["rows"][number],
): boolean {
  const hasWorkspaceRoot =
    typeof row.workspaceRoot === "string" && row.workspaceRoot.trim().length > 0;
  return hasWorkspaceRoot || row.status === "running";
}

function projectImportJobMatchesRuntimeRow(
  job: ProjectImportJob,
  row: CmmdForgeRuntimeListSnapshot["rows"][number],
): boolean {
  return (
    runtimeRowCanResolveProjectImport(row) &&
    (projectImportLabelMatchesRepository(job, row.githubRepoFullName) ||
      projectImportJobMatchesFallbackProjectName(job, row.projectName, [row.githubRepoFullName]))
  );
}

function projectImportJobHasResolvedCloudProject(
  job: ProjectImportJob,
  snapshot: CmmdForgeRuntimeListSnapshot,
  connectedProjects: readonly SidebarProjectSnapshot[],
  catalogMembershipIsAuthoritative: boolean,
): boolean {
  return (
    (!catalogMembershipIsAuthoritative &&
      connectedProjects.some((project) => projectImportJobMatchesConnectedProject(job, project))) ||
    snapshot.rows.some((row) => projectImportJobMatchesRuntimeRow(job, row))
  );
}

function projectImportJobIsExpired(job: ProjectImportJob, nowMs: number): boolean {
  if (job.status !== "cloning") return false;
  if (job.source === "blank") return false;

  const updatedAtMs = Date.parse(job.updatedAt);
  if (!Number.isFinite(updatedAtMs)) return false;

  return nowMs - updatedAtMs > STALE_PROJECT_IMPORT_JOB_TTL_MS;
}

export function resolveNextProjectImportExpiryAt(
  jobs: readonly ProjectImportJob[],
  nowMs: number,
): number | null {
  const expiryTimes = jobs.flatMap((job) => {
    if (job.destination !== "forge-cloud" || job.status !== "cloning" || job.source === "blank") {
      return [];
    }
    const updatedAtMs = Date.parse(job.updatedAt);
    const expiryAt = updatedAtMs + STALE_PROJECT_IMPORT_JOB_TTL_MS + 1;
    return Number.isFinite(updatedAtMs) && expiryAt > nowMs ? [expiryAt] : [];
  });
  return expiryTimes.length > 0 ? Math.min(...expiryTimes) : null;
}

export function resolveVisibleProjectImportJobs(input: {
  readonly jobs: readonly ProjectImportJob[];
  readonly snapshot: CmmdForgeRuntimeListSnapshot;
  readonly connectedCloudProjects: readonly SidebarProjectSnapshot[];
  readonly catalogMembershipIsAuthoritative?: boolean;
  readonly nowMs: number;
}): readonly ProjectImportJob[] {
  return input.jobs.filter((job) => {
    if (job.status === "ready") return false;
    if (job.destination !== "forge-cloud") return true;
    return (
      !projectImportJobIsExpired(job, input.nowMs) &&
      !projectImportJobHasResolvedCloudProject(
        job,
        input.snapshot,
        input.connectedCloudProjects,
        input.catalogMembershipIsAuthoritative ?? false,
      )
    );
  });
}

/**
 * Whether a visible import job already accounts for this catalog row, so a
 * provisioning row is not offered as pre-connect while its own clone is still
 * in progress.
 *
 * Exported for `components/projects/visibleCloudRows.ts`, the one place that
 * now decides which catalog rows a surface offers as pre-connect.
 * `resolveVisiblePreConnectCloudRows` used to apply this suppression
 * alongside a runtime-level exclusion; that half moved there and had to stop
 * being runtime-level (a per-owner Environment hosts every Registration its
 * owner has, so excluding by runtime dropped every sibling Registration the
 * instant one of them connected). See that module's doc comment.
 */
export function cloudProjectIsSuppressedByVisibleJob(
  name: string,
  githubRepoFullName: string | null,
  visibleCloudJobs: readonly ProjectImportJob[],
): boolean {
  return visibleCloudJobs.some(
    (job) =>
      projectImportLabelMatchesRepository(job, githubRepoFullName) ||
      projectImportJobMatchesFallbackProjectName(job, name, [githubRepoFullName]),
  );
}
